﻿export interface DocumentAuditPayload {
  organizationId: string;
  customerId?: string | null;
  caseId?: string | null;
  documentType: string;
  documentId?: string | null;
  actionType: string;
  channel: string;
  technicalSource: string;
  context?: Record<string, unknown>;
}

export interface RoleChangeAuditPayload {
  organizationId: string;
  targetUserId: string;
  actionType: string;
  technicalSource: string;
  oldValues?: Record<string, unknown>;
  newValues?: Record<string, unknown>;
  context?: Record<string, unknown>;
}

export interface SystemAuditEventPayload {
  organizationId: string | null;
  eventType: string;
  actionResult?: 'success' | 'failure' | 'blocked';
  actorUserId?: string | null;
  actorRole?: string | null;
  caseId?: string | null;
  documentId?: string | null;
  referenceId?: string | null;
  reasonRequired?: boolean;
  reason?: string | null;
  metadata?: Record<string, unknown>;
  userAgent?: string | null;
}

export interface PortalAuditEventPayload {
  customerId: string;
  eventType: string;
  actionResult?: 'success' | 'failure' | 'blocked';
  documentId?: string | null;
  referenceId?: string | null;
  reason?: string | null;
  metadata?: Record<string, unknown>;
  userAgent?: string | null;
}

export async function logDocumentAccessAudit(
  supabase: any,
  payload: DocumentAuditPayload,
): Promise<void> {
  try {
    const { error } = await supabase.rpc('log_document_access', {
      p_organization_id: payload.organizationId,
      p_customer_id: payload.customerId ?? null,
      p_case_id: payload.caseId ?? null,
      p_document_type: payload.documentType,
      p_document_id: payload.documentId ?? null,
      p_action_type: payload.actionType,
      p_channel: payload.channel,
      p_technical_source: payload.technicalSource,
      p_context: payload.context ?? {},
    });

    if (error) {
      // Best effort audit logging: business actions must continue.
      console.warn('Document access audit logging failed', error);
    }
  } catch (error) {
    console.warn('Document access audit logging threw', error);
  }
}

export async function logSecurityRoleChangeAudit(
  supabase: any,
  payload: RoleChangeAuditPayload,
): Promise<void> {
  try {
    const { error } = await supabase.rpc('log_security_role_change', {
      p_organization_id: payload.organizationId,
      p_target_user_id: payload.targetUserId,
      p_action_type: payload.actionType,
      p_technical_source: payload.technicalSource,
      p_old_values: payload.oldValues ?? {},
      p_new_values: payload.newValues ?? {},
      p_context: payload.context ?? {},
    });

    if (error) {
      // Best effort audit logging: business actions must continue.
      console.warn('Role change audit logging failed', error);
    }
  } catch (error) {
    console.warn('Role change audit logging threw', error);
  }
}

export async function logSystemAuditEvent(
  supabase: any,
  payload: SystemAuditEventPayload,
): Promise<void> {
  try {
    const { error } = await supabase.rpc('log_system_audit_event', {
      p_organization_id: payload.organizationId,
      p_event_type: payload.eventType,
      p_action_result: payload.actionResult ?? 'success',
      p_actor_user_id: payload.actorUserId ?? null,
      p_actor_role: payload.actorRole ?? null,
      p_case_id: payload.caseId ?? null,
      p_document_id: payload.documentId ?? null,
      p_reference_id: payload.referenceId ?? null,
      p_reason_required: payload.reasonRequired ?? false,
      p_reason: payload.reason ?? null,
      p_metadata: payload.metadata ?? {},
      p_user_agent: payload.userAgent ?? null,
    });

    if (error) {
      // Audit logging should not block business operations in the UI.
      console.warn('System audit logging failed', error);
    }
  } catch (error) {
    console.warn('System audit logging threw', error);
  }
}

export async function logPortalAuditEvent(
  supabase: any,
  payload: PortalAuditEventPayload,
): Promise<void> {
  try {
    const { error } = await supabase.rpc('log_portal_audit_event', {
      p_customer_id: payload.customerId,
      p_event_type: payload.eventType,
      p_action_result: payload.actionResult ?? 'success',
      p_document_id: payload.documentId ?? null,
      p_reference_id: payload.referenceId ?? null,
      p_reason: payload.reason ?? null,
      p_metadata: payload.metadata ?? {},
      p_user_agent: payload.userAgent ?? null,
    });

    if (error) {
      // Portal events are best effort in the client; the server still validates context.
      console.warn('Portal audit logging failed', error);
    }
  } catch (error) {
    console.warn('Portal audit logging threw', error);
  }
}

const organizationIdCache = new Map<string, string>();

export async function resolveCurrentUserOrganizationId(
  supabase: any,
  userId: string | null | undefined,
): Promise<string | null> {
  if (!userId) return null;
  const cached = organizationIdCache.get(userId);
  if (cached) return cached;

  try {
    const { data, error } = await supabase
      .from('organization_users')
      .select('organization_id')
      .eq('user_id', userId)
      .maybeSingle();

    if (error || !data?.organization_id) return null;
    organizationIdCache.set(userId, data.organization_id);
    return data.organization_id;
  } catch {
    return null;
  }
}

export async function logDocumentExportForCurrentUser(
  supabase: any,
  params: {
    userId: string | null | undefined;
    customerId?: string | null;
    caseId?: string | null;
    documentType: string;
    documentId?: string | null;
    actionType: string;
    channel: string;
    technicalSource: string;
    context?: Record<string, unknown>;
  },
): Promise<void> {
  const organizationId = await resolveCurrentUserOrganizationId(supabase, params.userId);
  if (!organizationId) return;

  await logDocumentAccessAudit(supabase, {
    organizationId,
    customerId: params.customerId ?? null,
    caseId: params.caseId ?? null,
    documentType: params.documentType,
    documentId: params.documentId ?? null,
    actionType: params.actionType,
    channel: params.channel,
    technicalSource: params.technicalSource,
    context: params.context ?? {},
  });
}
